let chartPromise = null;
function loadChartJs() {
  if (window.Chart) return Promise.resolve(window.Chart);
  if (chartPromise) return chartPromise;
  chartPromise = import('chart.js/auto')
    .then(({ default: Chart }) => {
      window.Chart = Chart;
      return Chart;
    })
    .catch((error) => {
      chartPromise = null;
      throw error;
    });
  return chartPromise;
}

let mapLibsPromise = null;
function loadMapLibs() {
  if (mapLibsPromise) return mapLibsPromise;
  mapLibsPromise = Promise.all([import('d3'), import('topojson-client')])
    .then(([d3, topojson]) => ({ d3, topojson }))
    .catch((error) => {
      mapLibsPromise = null;
      throw error;
    });
  return mapLibsPromise;
}

function loadWorldTopology() {
  return import('world-atlas/countries-110m.json').then(module => module.default);
}

function loadUsTopology() {
  return import('us-atlas/states-10m.json').then(module => module.default);
}

const _charts = new Map();
function destroyChart(key) {
  const c = _charts.get(key);
  if (c) { try { c.destroy(); } catch {} _charts.delete(key); }
}
function renderChart(key, canvas, config) {
  destroyChart(key);
  if (!window.Chart) throw new Error('Chart.js is not loaded');
  const chart = new window.Chart(canvas, config);
  _charts.set(key, chart);
  return chart;
}

const ACCENT  = '#00e5a0';
const ACCENT2 = '#00b8ff';
const DIM     = '#888';
const SURFACE = '#1a1a1f';
const PAGE_VIEW_SERIES_LIMIT = 5;
export const ANALYTICS_REQUEST_CONCURRENCY = 3;
const CHART_PALETTE = [ACCENT, ACCENT2, '#b58bff', '#ffb347', '#ff6b9d', '#ffdb5c'];
export const NETPLAY_MODE_META = Object.freeze({
  solo: { label: 'Solo', color: DIM },
  lockstep: { label: 'Lockstep', color: '#ffb347' },
  'link-cable': { label: 'Link cable', color: '#ff6b9d' },
  host: { label: 'Hosted (host)', color: ACCENT },
  guest: { label: 'Hosted (guest)', color: ACCENT2 },
  spectator: { label: 'Spectator', color: '#b58bff' },
});

/**
 * Create a shared request scheduler. Overlapping window changes join the same
 * queue, so an aborted older load cannot briefly double the concurrency of its
 * replacement while the browser finishes cancelling network work.
 */
export function createSettledTaskScheduler(limit = ANALYTICS_REQUEST_CONCURRENCY) {
  const concurrency = Math.max(1, Math.floor(limit) || 1);
  const queue = [];
  let active = 0;

  const drain = () => {
    while (active < concurrency && queue.length > 0) {
      const { task, resolve } = queue.shift();
      active++;
      Promise.resolve()
        .then(task)
        .then(
          value => resolve({ status: 'fulfilled', value }),
          reason => resolve({ status: 'rejected', reason }),
        )
        .finally(() => {
          active--;
          drain();
        });
    }
  };

  return (tasks) => {
    if (!Array.isArray(tasks) || tasks.length === 0) return Promise.resolve([]);
    const pending = tasks.map(task => new Promise((resolve) => {
      queue.push({ task, resolve });
    }));
    drain();
    return Promise.all(pending);
  };
}

const settleAnalyticsRequests = createSettledTaskScheduler();

/**
 * Return a new options tree for every Chart instance.
 *
 * Chart.js normalises its configuration in place. Sharing the old
 * DEFAULT_OPTIONS object meant one chart could rewrite the scales used by a
 * chart rendered later (the horizontal Top Games chart was the most visible
 * casualty). Keep every nested object fresh, including fonts and tick config.
 */
export function createChartOptions(overrides = {}) {
  const base = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { labels: { color: '#ccc', font: { family: 'JetBrains Mono, monospace', size: 11 } } },
      tooltip: {
        titleFont: { family: 'JetBrains Mono' },
        bodyFont: { family: 'JetBrains Mono' },
        callbacks: {},
      },
    },
    scales: {
      x: {
        ticks: {
          color: DIM,
          font: { family: 'JetBrains Mono', size: 11 },
          autoSkip: true,
          maxTicksLimit: 12,
          maxRotation: 0,
        },
        grid: { color: '#2a2a2a' },
      },
      y: {
        ticks: { color: DIM, font: { family: 'JetBrains Mono', size: 11 } },
        grid: { color: '#2a2a2a' },
        beginAtZero: true,
      },
    },
  };
  const mergeAxis = (name) => ({
    ...base.scales[name],
    ...overrides.scales?.[name],
    ticks: {
      ...base.scales[name].ticks,
      ...overrides.scales?.[name]?.ticks,
      font: {
        ...base.scales[name].ticks.font,
        ...overrides.scales?.[name]?.ticks?.font,
      },
    },
    grid: { ...base.scales[name].grid, ...overrides.scales?.[name]?.grid },
  });
  return {
    ...base,
    ...overrides,
    interaction: { ...base.interaction, ...overrides.interaction },
    plugins: {
      ...base.plugins,
      ...overrides.plugins,
      legend: {
        ...base.plugins.legend,
        ...overrides.plugins?.legend,
        labels: {
          ...base.plugins.legend.labels,
          ...overrides.plugins?.legend?.labels,
          font: {
            ...base.plugins.legend.labels.font,
            ...overrides.plugins?.legend?.labels?.font,
          },
        },
      },
      tooltip: {
        ...base.plugins.tooltip,
        ...overrides.plugins?.tooltip,
        callbacks: {
          ...base.plugins.tooltip.callbacks,
          ...overrides.plugins?.tooltip?.callbacks,
        },
      },
    },
    scales: { ...overrides.scales, x: mergeAxis('x'), y: mergeAxis('y') },
  };
}

function fmtInt(n) { return (n || 0).toLocaleString(); }
function fmt1(n) { return (n == null || !Number.isFinite(n)) ? '–' : (Math.round(n * 10) / 10).toLocaleString(); }

export function fmtDuration(seconds) {
  const value = Math.max(0, Number(seconds) || 0);
  if (value < 60) return `${Math.round(value)} sec`;
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  if (!hours) return `${minutes} min`;
  return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
}

function chartStateElement(canvas) {
  const id = `${canvas.id || 'analytics-chart'}-state`;
  let state = document.getElementById(id);
  if (!state) {
    state = document.createElement('div');
    state.id = id;
    state.className = 'analytics-chart-state';
    state.setAttribute('role', 'status');
    state.setAttribute('aria-live', 'polite');
    canvas.insertAdjacentElement('afterend', state);
  }
  return state;
}

function setChartState(canvas, kind, message = '', retry = null) {
  const state = chartStateElement(canvas);
  state.replaceChildren();
  state.dataset.state = kind;
  canvas.hidden = kind !== 'ready';
  if (kind === 'ready') {
    state.hidden = true;
    return;
  }
  state.hidden = false;
  const text = document.createElement('span');
  text.textContent = message;
  state.appendChild(text);
  if (retry) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-mode analytics-retry';
    button.textContent = 'Retry';
    button.addEventListener('click', retry, { once: true });
    state.appendChild(button);
  }
}

function setPanelState(root, message, retry = null) {
  root.replaceChildren();
  const state = document.createElement('div');
  state.className = 'analytics-chart-state';
  state.setAttribute('role', 'status');
  state.setAttribute('aria-live', 'polite');
  const text = document.createElement('span');
  text.textContent = message;
  state.appendChild(text);
  if (retry) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-mode analytics-retry';
    button.textContent = 'Retry';
    button.addEventListener('click', retry, { once: true });
    state.appendChild(button);
  }
  root.appendChild(state);
}

async function fetchJson(url, { signal } = {}) {
  const r = await fetch(url, { credentials: 'include', signal });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json();
}

export function createWindowLoadCoordinator() {
  let generation = 0;
  let controller = null;
  let current = null;
  return {
    begin(days) {
      controller?.abort();
      controller = new AbortController();
      current = { days, generation: ++generation, signal: controller.signal };
      return current;
    },
    current() { return current; },
    isCurrent(request) {
      return Boolean(request)
        && !request.signal.aborted
        && request.generation === generation
        && request === current;
    },
  };
}

const windowLoads = createWindowLoadCoordinator();

function currentWindowRequest() {
  return windowLoads.current() || windowLoads.begin(activeAnalyticsDays());
}

function staleWindowRequest(request) {
  return !windowLoads.isCurrent(request);
}

function isAborted(error, request) {
  return error?.name === 'AbortError' || staleWindowRequest(request);
}

export function paintCoverage(id, coverage, label) {
  const root = document.getElementById(id);
  if (!root) return;
  const truncated = coverage?.truncated === true;
  root.hidden = !truncated;
  root.textContent = truncated
    ? `${coverage.requested_days} days selected; ${label} is retained for ${coverage.retention_days} days, so this panel covers only the latest ${coverage.effective_days} days.`
    : '';
}

export function renderSourceDataTable(id, { caption, columns, rows }) {
  const root = document.getElementById(id);
  if (!root) return;
  root.hidden = false;
  const summary = document.createElement('summary');
  summary.textContent = 'View source data';
  const scroll = document.createElement('div');
  scroll.className = 'analytics-source-data-scroll';
  const table = document.createElement('table');
  table.className = 'analytics-source-data-table';
  const tableCaption = document.createElement('caption');
  tableCaption.textContent = caption;
  table.appendChild(tableCaption);
  const thead = document.createElement('thead');
  const head = document.createElement('tr');
  for (const column of columns) {
    const th = document.createElement('th');
    th.scope = 'col';
    th.textContent = column;
    head.appendChild(th);
  }
  thead.appendChild(head);
  table.appendChild(thead);
  const tbody = document.createElement('tbody');
  for (const values of rows) {
    const tr = document.createElement('tr');
    values.forEach((value, index) => {
      const cell = document.createElement(index === 0 ? 'th' : 'td');
      if (index === 0) cell.scope = 'row';
      cell.textContent = value == null ? '–' : String(value);
      tr.appendChild(cell);
    });
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  scroll.appendChild(table);
  root.replaceChildren(summary, scroll);
}

function clearSourceData(id) {
  const root = document.getElementById(id);
  if (!root) return;
  root.hidden = true;
  root.replaceChildren();
}

let refreshPollTimer = null;
let refreshNeedsReload = false;
let analyticsDays = 90;

function updateWindowLabels() {
  document.querySelectorAll('[data-analytics-window-label]').forEach((el) => {
    el.textContent = `${analyticsDays} days`;
  });
}

function activeAnalyticsDays() {
  const selected = Number(document.querySelector('[data-analytics-days][aria-pressed="true"]')?.dataset.analyticsDays);
  analyticsDays = [7, 30, 90, 365].includes(selected) ? selected : analyticsDays;
  return analyticsDays;
}

function paintRefreshStatus(job) {
  const status = document.getElementById('an-refresh-status');
  const button = document.getElementById('an-refresh-btn');
  if (!status || !button) return;
  button.disabled = job?.status === 'queued' || job?.status === 'running';
  if (!job) {
    status.textContent = 'No refresh has run yet.';
    return;
  }
  const when = job.completed_at || job.started_at || job.requested_at;
  const stamp = when ? new Date(when).toLocaleString() : '';
  status.textContent = job.status === 'completed'
    ? `Last refresh completed ${stamp}`
    : job.status === 'failed'
      ? `Last refresh failed ${stamp}: ${job.error || 'see view results'}`
      : `Refresh ${job.status}${stamp ? ` since ${stamp}` : ''}…`;
}

async function loadRefreshStatus({ poll = true } = {}) {
  if (refreshPollTimer) clearTimeout(refreshPollTimer);
  refreshPollTimer = null;
  try {
    const { job } = await fetchJson('/api/analytics/refresh');
    paintRefreshStatus(job);
    if (poll && (job?.status === 'queued' || job?.status === 'running')) {
      refreshPollTimer = setTimeout(
        () => settleAnalyticsRequests([() => loadRefreshStatus()]),
        2_000,
      );
    } else if (job?.status === 'completed' && refreshNeedsReload) {
      refreshNeedsReload = false;
      await loadAnalyticsData();
      const status = document.getElementById('an-refresh-status');
      if (status) status.textContent = `Last refresh completed ${new Date(job.completed_at || Date.now()).toLocaleString()}; dashboard reloaded.`;
    } else if (job?.status === 'failed') {
      refreshNeedsReload = false;
    }
  } catch {
    const status = document.getElementById('an-refresh-status');
    if (status) status.textContent = 'Refresh status unavailable.';
  }
}

async function triggerRefresh() {
  const button = document.getElementById('an-refresh-btn');
  if (button) button.disabled = true;
  try {
    const response = await fetch('/api/analytics/refresh', {
      method: 'POST', credentials: 'include',
    });
    if (!response.ok) throw new Error(`refresh → ${response.status}`);
    const { job } = await response.json();
    refreshNeedsReload = true;
    paintRefreshStatus(job);
    await loadRefreshStatus();
  } catch {
    if (button) button.disabled = false;
    const status = document.getElementById('an-refresh-status');
    if (status) status.textContent = 'Could not queue refresh.';
  }
}

// ── Section loaders (identical to original analytics.js) ────

async function loadSummary() {
  try {
    const data = await fetchJson('/api/analytics/summary');
    document.getElementById('an-tile-users').textContent    = fmtInt(data.users);
    document.getElementById('an-tile-sessions').textContent = fmtInt(data.sessions);
    document.getElementById('an-tile-zero-duration-attempts').textContent = fmtInt(data.recovered_zero_duration_attempts);
    document.getElementById('an-tile-hours').textContent    = fmtDuration(data.total_play_seconds);
    document.getElementById('an-tile-today').textContent    = fmtInt(data.active_today);
    document.getElementById('an-tile-unlocks').textContent  = fmtInt(data.achievement_unlocks);
  } catch {
    for (const id of ['an-tile-users','an-tile-sessions','an-tile-zero-duration-attempts','an-tile-hours','an-tile-today','an-tile-unlocks']) {
      const el = document.getElementById(id);
      if (el) el.textContent = '–';
    }
  }
}

async function loadDau(request = currentWindowRequest()) {
  const { days, signal } = request;
  const canvas = document.getElementById('an-chart-dau');
  if (!canvas || staleWindowRequest(request)) return;
  clearSourceData('an-data-dau');
  setChartState(canvas, 'loading', 'Loading signed-in activity…');
  try {
    await loadChartJs();
    if (staleWindowRequest(request)) return;
    const data = await fetchJson(`/api/analytics/dau?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    if (!data.some(row => (row.dau || 0) + (row.wau || 0) + (row.mau || 0) > 0)) {
      destroyChart('dau');
      setChartState(canvas, 'empty', `No signed-in activity in the last ${days} days.`);
      return;
    }
    renderSourceDataTable('an-data-dau', {
      caption: `Signed-in player activity for the latest ${days} UTC calendar days.`,
      columns: ['Date', 'DAU', 'WAU', 'MAU'],
      rows: data.map(row => [row.day, fmtInt(row.dau), fmtInt(row.wau), fmtInt(row.mau)]),
    });
    setChartState(canvas, 'ready');
    renderChart('dau', canvas, {
      type: 'line',
      data: {
        labels: data.map(r => r.day),
        datasets: [
          { label: 'DAU', data: data.map(r => r.dau), borderColor: ACCENT,  backgroundColor: ACCENT,  tension: 0.25, pointRadius: 0 },
          { label: 'WAU', data: data.map(r => r.wau), borderColor: ACCENT2, backgroundColor: ACCENT2, tension: 0.25, pointRadius: 0 },
          { label: 'MAU', data: data.map(r => r.mau), borderColor: '#b58bff', backgroundColor: '#b58bff', tension: 0.25, pointRadius: 0 },
        ],
      },
      options: createChartOptions(),
    });
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] dau load failed:', err);
    destroyChart('dau');
    setChartState(canvas, 'error', 'Could not load signed-in activity.', () => loadDau(request));
  }
}

async function loadHeatmap(request = currentWindowRequest()) {
  const { days, signal } = request;
  const root = document.getElementById('an-heatmap');
  if (!root || staleWindowRequest(request)) return;
  setPanelState(root, 'Loading session-start times…');
  try {
    const { grid } = await fetchJson(`/api/analytics/heatmap?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    let max = 0;
    for (const row of grid) for (const cell of row) if (cell.sessions > max) max = cell.sessions;
    if (max === 0) {
      setPanelState(root, `No signed-in session starts in the last ${days} days.`);
      return;
    }
    const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    root.innerHTML = '';
    const corner = document.createElement('div');
    root.appendChild(corner);
    for (let h = 0; h < 24; h++) {
      const hdr = document.createElement('div');
      hdr.className = 'analytics-heatmap-hour-header';
      hdr.textContent = (h % 3 === 0) ? h : '';
      root.appendChild(hdr);
    }
    for (let d = 0; d < 7; d++) {
      const label = document.createElement('div');
      label.className = 'analytics-heatmap-dow-label';
      label.textContent = DOW_LABELS[d];
      root.appendChild(label);
      for (let h = 0; h < 24; h++) {
        const cell = document.createElement('div');
        cell.className = 'analytics-heatmap-cell';
        const n = grid[d][h].sessions;
        const intensity = max > 0 ? n / max : 0;
        cell.style.background = n === 0 ? SURFACE : `rgba(0, 229, 160, ${0.15 + intensity * 0.75})`;
        cell.title = `${DOW_LABELS[d]} ${String(h).padStart(2,'0')}:00 · ${n} session${n === 1 ? '' : 's'}`;
        if (n > 0) {
          cell.tabIndex = 0;
          cell.setAttribute('role', 'img');
          cell.setAttribute('aria-label', cell.title);
        }
        root.appendChild(cell);
      }
    }
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] heatmap load failed:', err);
    setPanelState(root, 'Could not load session-start times.', () => loadHeatmap(request));
  }
}

export function makeTopGamesChartConfig(data, by) {
  const labels = data.map(r => r.title || `#${r.game_id}`);
  const isTime = by === 'time';
  const values = data.map(r => isTime ? (r.total_play_seconds || 0) : (r.players || 0));
  return {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: isTime ? 'Play time' : 'Signed-in players',
        data: values,
        backgroundColor: ACCENT,
        borderColor: ACCENT,
        borderWidth: 1,
      }],
    },
    options: createChartOptions({
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: isTime ? { callbacks: { label: (context) => `Play time: ${fmtDuration(context.raw)}` } } : {},
      },
      scales: isTime ? { x: { ticks: { callback: (value) => fmtDuration(value) } } } : {},
    }),
  };
}

function topGamesModeIsCurrent(by) {
  return document.querySelector('.analytics-tab[data-topgames-by].active')?.dataset.topgamesBy === by;
}

async function loadTopGames(by = 'players', request = currentWindowRequest()) {
  const { days, signal } = request;
  const canvas = document.getElementById('an-chart-top-games');
  if (!canvas || staleWindowRequest(request) || !topGamesModeIsCurrent(by)) return;
  clearSourceData('an-data-top-games');
  setChartState(canvas, 'loading', 'Loading top games…');
  try {
    await loadChartJs();
    if (staleWindowRequest(request) || !topGamesModeIsCurrent(by)) return;
    const data = await fetchJson(`/api/analytics/top-games?by=${by}&limit=15&days=${days}`, { signal });
    if (staleWindowRequest(request) || !topGamesModeIsCurrent(by)) return;
    if (!data.length) {
      destroyChart('topGames');
      setChartState(canvas, 'empty', `No signed-in play in the last ${days} days.`);
      return;
    }
    const isTime = by === 'time';
    renderSourceDataTable('an-data-top-games', {
      caption: `Top signed-in games by ${isTime ? 'play time' : 'distinct players'} for the latest ${days} days.`,
      columns: ['Game', isTime ? 'Play time (seconds)' : 'Signed-in players'],
      rows: data.map(row => [row.title || `#${row.game_id}`, isTime ? fmtInt(row.total_play_seconds) : fmtInt(row.players)]),
    });
    setChartState(canvas, 'ready');
    renderChart('topGames', canvas, makeTopGamesChartConfig(data, by));
  } catch (err) {
    if (isAborted(err, request) || !topGamesModeIsCurrent(by)) return;
    console.warn('[analytics] top-games load failed:', err);
    destroyChart('topGames');
    setChartState(canvas, 'error', 'Could not load top games.', () => loadTopGames(by, request));
  }
}

export function makeSystemsChartConfig(data) {
  const sorted = [...data].sort((a, b) => (b.total_seconds || 0) - (a.total_seconds || 0));
  return {
    type: 'bar',
    data: {
      labels: sorted.map(r => String(r.system_id || 'unknown').toUpperCase()),
      datasets: [{
        label: 'Play time',
        data: sorted.map(r => r.total_seconds || 0),
        backgroundColor: ACCENT2,
        borderColor: ACCENT2,
        borderWidth: 1,
      }],
    },
    options: createChartOptions({
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (context) => `Play time: ${fmtDuration(context.raw)}` } },
      },
      scales: { x: { ticks: { callback: (value) => fmtDuration(value) } } },
    }),
  };
}

async function loadSystems(request = currentWindowRequest()) {
  const { days, signal } = request;
  const canvas = document.getElementById('an-chart-systems');
  if (!canvas || staleWindowRequest(request)) return;
  clearSourceData('an-data-systems');
  setChartState(canvas, 'loading', 'Loading system play time…');
  try {
    await loadChartJs();
    if (staleWindowRequest(request)) return;
    const data = await fetchJson(`/api/analytics/systems?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    if (!data.some(row => (row.total_seconds || 0) > 0)) {
      destroyChart('systems');
      setChartState(canvas, 'empty', `No signed-in play time in the last ${days} days.`);
      return;
    }
    const sorted = [...data].sort((a, b) => (b.total_seconds || 0) - (a.total_seconds || 0));
    renderSourceDataTable('an-data-systems', {
      caption: `Signed-in play time by system for the latest ${days} days.`,
      columns: ['System', 'Play time (seconds)'],
      rows: sorted.map(row => [String(row.system_id || 'unknown').toUpperCase(), fmtInt(row.total_seconds)]),
    });
    setChartState(canvas, 'ready');
    renderChart('systems', canvas, makeSystemsChartConfig(data));
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] systems load failed:', err);
    destroyChart('systems');
    setChartState(canvas, 'error', 'Could not load system play time.', () => loadSystems(request));
  }
}

async function loadNetplay(request = currentWindowRequest()) {
  const { days, signal } = request;
  const root = document.getElementById('an-netplay');
  if (!root || staleWindowRequest(request)) return;
  paintCoverage('an-netplay-coverage', null, 'Netplay quality telemetry');
  setPanelState(root, 'Loading net mode player sessions…');
  try {
    const { adoption, quality, coverage } = await fetchJson(`/api/analytics/netplay?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    paintCoverage('an-netplay-coverage', coverage?.quality, 'Netplay quality telemetry');
    const playerSessionsFor = (row) => row.player_sessions ?? row.sessions ?? 0;
    if (!adoption.some(row => playerSessionsFor(row) > 0 || (row.recovered_zero_duration_attempts || 0) > 0)
        && !(quality.samples > 0)) {
      setPanelState(root, `No signed-in net mode sessions in the last ${days} days.`);
      return;
    }
    const totalSessions = adoption.reduce((sum, row) => sum + playerSessionsFor(row), 0);
    let adoptionBar = null;
    if (totalSessions > 0) {
      adoptionBar = document.createElement('div');
      adoptionBar.className = 'analytics-netplay-adoption';
      for (const row of adoption) {
        const meta = NETPLAY_MODE_META[row.net_mode] || { label: row.net_mode, color: DIM };
        const seg = document.createElement('div');
        seg.className = 'analytics-netplay-adoption-seg';
        seg.style.flexBasis = `${(playerSessionsFor(row) / totalSessions) * 100}%`;
        seg.style.background = meta.color;
        seg.title = `${meta.label}: ${playerSessionsFor(row)} qualified player sessions`;
        adoptionBar.appendChild(seg);
      }
    }
    const rows = [];
    if (adoptionBar) rows.push({ label: 'ADOPTION', dom: adoptionBar });
    for (const row of adoption) {
      const meta = NETPLAY_MODE_META[row.net_mode] || { label: row.net_mode };
      // Solo sessions do not have rooms. Suppressing a literal "0 rooms" keeps
      // the row from implying that a room count is a relevant solo metric.
      const roomText = row.rooms > 0 ? ` · ${fmtInt(row.rooms)} rooms` : '';
      const zeroDurationAttempts = row.recovered_zero_duration_attempts || 0;
      const attemptText = zeroDurationAttempts > 0
        ? ` · ${fmtInt(zeroDurationAttempts)} recovered zero-duration attempt${zeroDurationAttempts === 1 ? '' : 's'}` : '';
      rows.push({
        label: meta.label,
        value: `${fmtInt(playerSessionsFor(row))} qualified player sessions${roomText}${attemptText}`,
      });
    }
    rows.push({ label: 'QUALITY SESSIONS', value: fmtInt(quality.sessions) });
    rows.push({ label: 'RTT SAMPLES', value: fmtInt(quality.rtt_samples) });
    rows.push({ label: 'FPS SAMPLES', value: fmtInt(quality.fps_samples) });
    rows.push({ label: 'AVG RTT', value: quality.avg_rtt_ms == null ? '–' : `${fmt1(quality.avg_rtt_ms)} ms` });
    rows.push({ label: 'P95 RTT', value: quality.p95_rtt_ms == null ? '–' : `${fmt1(quality.p95_rtt_ms)} ms` });
    rows.push({ label: 'AVG ROLLBACKS', value: fmt1(quality.avg_rollbacks) });
    rows.push({ label: 'AVG LOCKSTEP STALLS', value: fmt1(quality.avg_stalls) });
    rows.push({ label: 'AVG DESYNCS', value: fmt1(quality.avg_desyncs) });
    rows.push({ label: 'AVG FPS', value: fmt1(quality.avg_fps) });
    root.innerHTML = '';
    for (const r of rows) {
      const row = document.createElement('div');
      row.className = 'analytics-netplay-row';
      const lbl = document.createElement('span');
      lbl.className = 'analytics-netplay-label';
      lbl.textContent = r.label;
      row.appendChild(lbl);
      if (r.dom) { row.appendChild(r.dom); }
      else { const v = document.createElement('span'); v.className = 'analytics-netplay-value'; v.textContent = r.value; row.appendChild(v); }
      root.appendChild(row);
    }
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] netplay load failed:', err);
    setPanelState(root, 'Could not load net mode metrics.', () => loadNetplay(request));
  }
}

export function gapFillDailyRows(data, days, endDay = new Date().toISOString().slice(0, 10)) {
  const rows = new Map(data.map(row => [String(row.day).slice(0, 10), row]));
  const end = new Date(`${endDay}T00:00:00Z`);
  const out = [];
  for (let offset = days - 1; offset >= 0; offset--) {
    const date = new Date(end);
    date.setUTCDate(end.getUTCDate() - offset);
    const day = date.toISOString().slice(0, 10);
    out.push(rows.get(day) || { day, avg_rtt: null, p95_rtt: null, samples: 0 });
  }
  return out;
}

async function loadNetplayRtt(request = currentWindowRequest()) {
  const { days, signal } = request;
  const canvas = document.getElementById('an-chart-netplay-rtt');
  if (!canvas || staleWindowRequest(request)) return;
  clearSourceData('an-data-netplay-rtt');
  paintCoverage('an-netplay-rtt-coverage', null, 'RTT telemetry');
  setChartState(canvas, 'loading', 'Loading RTT samples…');
  try {
    await loadChartJs();
    if (staleWindowRequest(request)) return;
    const { series, coverage } = await fetchJson(`/api/analytics/netplay/rtt?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    paintCoverage('an-netplay-rtt-coverage', coverage, 'RTT telemetry');
    const effectiveDays = coverage?.effective_days || days;
    if (!series.some(row => (row.samples || 0) > 0)) {
      destroyChart('netplay-rtt');
      setChartState(canvas, 'empty', `No RTT samples in the available ${effectiveDays}-day coverage.`);
      return;
    }
    const rows = gapFillDailyRows(series, effectiveDays);
    const samples = rows.map(row => row.samples || 0);
    renderSourceDataTable('an-data-netplay-rtt', {
      caption: `Netplay round-trip-time observations for the latest ${effectiveDays} days. Missing days had no recorded samples.`,
      columns: ['Date', 'Average RTT (ms)', 'P95 RTT (ms)', 'Samples'],
      rows: rows.map(row => [row.day, row.avg_rtt == null ? '–' : fmt1(row.avg_rtt), row.p95_rtt == null ? '–' : fmt1(row.p95_rtt), fmtInt(row.samples)]),
    });
    setChartState(canvas, 'ready');
    renderChart('netplay-rtt', canvas, {
      type: 'line',
      data: { labels: rows.map(r => r.day), datasets: [
        { label: 'Avg RTT (ms)', data: rows.map(r => r.avg_rtt), borderColor: ACCENT, backgroundColor: ACCENT, tension: 0.25, pointRadius: 0, spanGaps: false },
        { label: 'P95 RTT (ms)', data: rows.map(r => r.p95_rtt), borderColor: ACCENT2, backgroundColor: ACCENT2, tension: 0.25, pointRadius: 0, spanGaps: false },
      ]},
      options: createChartOptions({
        plugins: {
          tooltip: {
            callbacks: {
              footer: (items) => items.length ? `${fmtInt(samples[items[0].dataIndex])} samples` : '',
            },
          },
        },
      }),
    });
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] netplay/rtt load failed:', err);
    destroyChart('netplay-rtt');
    setChartState(canvas, 'error', 'Could not load RTT samples.', () => loadNetplayRtt(request));
  }
}

async function loadAchievements(request = currentWindowRequest()) {
  const { days, signal } = request;
  const canvas = document.getElementById('an-chart-achievements');
  if (!canvas || staleWindowRequest(request)) return;
  clearSourceData('an-data-achievements');
  setChartState(canvas, 'loading', 'Loading achievement activity…');
  try {
    await loadChartJs();
    if (staleWindowRequest(request)) return;
    const data = await fetchJson(`/api/analytics/achievements?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    if (!data.some(row => (row.unlocks || 0) + (row.hardcore_unlocks || 0) + (row.unique_users || 0) > 0)) {
      destroyChart('ach');
      setChartState(canvas, 'empty', `No achievement activity in the last ${days} days.`);
      return;
    }
    renderSourceDataTable('an-data-achievements', {
      caption: `Signed-in achievement activity for the latest ${days} days.`,
      columns: ['Date', 'Normal unlocks', 'Hardcore unlocks', 'Unique unlockers'],
      rows: data.map(row => [
        row.day,
        fmtInt(Math.max(0, (row.unlocks || 0) - (row.hardcore_unlocks || 0))),
        fmtInt(row.hardcore_unlocks),
        fmtInt(row.unique_users),
      ]),
    });
    setChartState(canvas, 'ready');
    renderChart('ach', canvas, {
      type: 'line',
      data: { labels: data.map(r => r.day), datasets: [
        { label: 'Normal unlocks', data: data.map(r => Math.max(0, (r.unlocks || 0) - (r.hardcore_unlocks || 0))), borderColor: ACCENT, backgroundColor: ACCENT, tension: 0.25, pointRadius: 0 },
        { label: 'Hardcore unlocks', data: data.map(r => r.hardcore_unlocks), borderColor: ACCENT2, backgroundColor: ACCENT2, tension: 0.25, pointRadius: 0 },
        { label: 'Unique unlockers', data: data.map(r => r.unique_users), borderColor: '#b58bff', backgroundColor: '#b58bff', borderDash: [5, 4], tension: 0.25, pointRadius: 0 },
      ]},
      options: createChartOptions(),
    });
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] achievements load failed:', err);
    destroyChart('ach');
    setChartState(canvas, 'error', 'Could not load achievement activity.', () => loadAchievements(request));
  }
}

async function loadSignups(request = currentWindowRequest()) {
  const { days, signal } = request;
  const canvas = document.getElementById('an-chart-signups');
  if (!canvas || staleWindowRequest(request)) return;
  clearSourceData('an-data-signups');
  setChartState(canvas, 'loading', 'Loading registrations…');
  try {
    await loadChartJs();
    if (staleWindowRequest(request)) return;
    const data = await fetchJson(`/api/analytics/signups?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    if (!data.some(row => (row.signups || 0) + (row.verified_signups || 0) > 0)) {
      destroyChart('signups');
      setChartState(canvas, 'empty', `No registrations in the last ${days} days.`);
      return;
    }
    renderSourceDataTable('an-data-signups', {
      caption: `New registered and verified accounts for the latest ${days} days.`,
      columns: ['Date', 'Registered accounts', 'Verified accounts'],
      rows: data.map(row => [row.day, fmtInt(row.signups), fmtInt(row.verified_signups)]),
    });
    setChartState(canvas, 'ready');
    renderChart('signups', canvas, {
      type: 'line',
      data: { labels: data.map(r => r.day), datasets: [
        { label: 'Registered accounts', data: data.map(r => r.signups), borderColor: ACCENT, backgroundColor: ACCENT, tension: 0.25, pointRadius: 0 },
        { label: 'Verified accounts', data: data.map(r => r.verified_signups || 0), borderColor: ACCENT2, backgroundColor: ACCENT2, tension: 0.25, pointRadius: 0 },
      ] },
      options: createChartOptions(),
    });
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] signups load failed:', err);
    destroyChart('signups');
    setChartState(canvas, 'error', 'Could not load registrations.', () => loadSignups(request));
  }
}

export function buildPageViewChartData(series, limit = PAGE_VIEW_SERIES_LIMIT) {
  const dayMap = new Map();
  const pageTotals = new Map();
  for (const row of series) {
    if (!dayMap.has(row.day)) dayMap.set(row.day, new Map());
    dayMap.get(row.day).set(row.page, row.views || 0);
    pageTotals.set(row.page, (pageTotals.get(row.page) || 0) + (row.views || 0));
  }
  const days = [...dayMap.keys()].sort();
  const rankedPages = [...pageTotals.keys()].sort((a, b) => pageTotals.get(b) - pageTotals.get(a) || a.localeCompare(b));
  const topPages = rankedPages.slice(0, limit);
  const otherPages = new Set(rankedPages.slice(limit));
  const datasets = topPages.map((page, index) => ({
    label: page,
    data: days.map(day => dayMap.get(day)?.get(page) || 0),
    borderColor: CHART_PALETTE[index],
    backgroundColor: CHART_PALETTE[index],
    tension: 0.25,
    pointRadius: 0,
  }));
  if (otherPages.size) {
    datasets.push({
      label: 'Other',
      data: days.map(day => [...otherPages].reduce((sum, page) => sum + (dayMap.get(day)?.get(page) || 0), 0)),
      borderColor: CHART_PALETTE[PAGE_VIEW_SERIES_LIMIT],
      backgroundColor: CHART_PALETTE[PAGE_VIEW_SERIES_LIMIT],
      tension: 0.25,
      pointRadius: 0,
    });
  }
  return { days, datasets };
}

export function renderPageViewTotals(root, totals, bots = []) {
  root.replaceChildren();
  for (const row of totals) {
    const r = document.createElement('div');
    r.className = 'analytics-pageviews-row';
    const name = document.createElement('span');
    name.className = 'analytics-pageviews-name';
    name.textContent = row.page;
    const views = document.createElement('span');
    views.className = 'analytics-pageviews-count';
    views.textContent = `${fmtInt(row.total_views)} recorded views`;
    const users = document.createElement('span');
    users.className = 'analytics-pageviews-users';
    users.textContent = `${fmtInt(row.signed_in_unique_users)} signed-in users`;
    const breakdown = document.createElement('dl');
    breakdown.className = 'analytics-pageviews-breakdown';
    for (const [label, field] of [
      ['Signed-in views', 'signed_in_views'],
      ['Anonymous views', 'anonymous_views'],
      ['Identified bot views', 'bot_views'],
      ['Unclassified views', 'unclassified_views'],
    ]) {
      const metric = document.createElement('div');
      const term = document.createElement('dt');
      term.textContent = label;
      const value = document.createElement('dd');
      value.textContent = fmtInt(row[field]);
      metric.append(term, value);
      breakdown.appendChild(metric);
    }
    r.append(name, views, users, breakdown);
    root.appendChild(r);
  }
  const agents = document.createElement('p');
  agents.className = 'analytics-card-note';
  agents.textContent = bots.length
    ? `Bot agents reported in this window: ${bots.map(bot => `${bot.bot_name} (${fmtInt(bot.views)})`).join(', ')}.`
    : 'No identified bot views in this window.';
  root.appendChild(agents);
}

async function loadPageViews(request = currentWindowRequest()) {
  const { days: daysWindow, signal } = request;
  const canvas = document.getElementById('an-chart-pageviews');
  const listEl = document.getElementById('an-pageviews-totals');
  if ((!canvas && !listEl) || staleWindowRequest(request)) return;
  clearSourceData('an-data-pageviews');
  paintCoverage('an-pageviews-coverage', null, 'Tracked page views');
  if (canvas) setChartState(canvas, 'loading', 'Loading tracked page views…');
  if (listEl) setPanelState(listEl, 'Loading top tracked pages…');
  try {
    const { totals, series, bots, coverage } = await fetchJson(`/api/analytics/page-views?days=${daysWindow}`, { signal });
    if (staleWindowRequest(request)) return;
    paintCoverage('an-pageviews-coverage', coverage, 'Tracked page views');
    const effectiveDays = coverage?.effective_days || daysWindow;
    if (listEl) {
      if (totals.length === 0) { setPanelState(listEl, `No tracked page views in the available ${effectiveDays}-day coverage.`); }
      else {
        renderPageViewTotals(listEl, totals, bots);
      }
    }
    if (canvas && series.some(row => (row.views || 0) > 0)) {
      await loadChartJs();
      if (staleWindowRequest(request)) return;
      const { days, datasets } = buildPageViewChartData(series);
      renderSourceDataTable('an-data-pageviews', {
        caption: `Views excluding identified bots for the latest ${effectiveDays} days. Unclassified traffic is included. Other combines all pages outside the top five.`,
        columns: ['Date', ...datasets.map(dataset => dataset.label)],
        rows: days.map((day, index) => [day, ...datasets.map(dataset => fmtInt(dataset.data[index]))]),
      });
      setChartState(canvas, 'ready');
      renderChart('pageviews', canvas, { type: 'line', data: { labels: days, datasets }, options: createChartOptions() });
    } else if (canvas) {
      destroyChart('pageviews');
      setChartState(canvas, 'empty', `No views excluding identified bots in the available ${effectiveDays}-day coverage.`);
    }
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] page-views load failed:', err);
    if (canvas) {
      destroyChart('pageviews');
      setChartState(canvas, 'error', 'Could not load tracked page views.', () => loadPageViews(request));
    }
    if (listEl) setPanelState(listEl, 'Could not load top tracked pages.', () => loadPageViews(request));
  }
}

// ── Funnels (RP-E15) ────────────────────────────────────────
//
// Rendered as a real <table> rather than a stack of styled divs: the numbers
// are the content, and a screen reader has to be able to read a step against
// its count and drop-off. The proportional bar is decoration layered inside the
// step cell and is aria-hidden, so it adds no noise.
//
// Every figure is labelled with what produced it. A client-emitted step can be
// lost (ad blocker, tab closed mid-load) and therefore under-counts, while a
// server-emitted step is authoritative — without that distinction an admin
// would read a collection gap as a product problem.

function fmtPct(n) {
  return (n == null || !Number.isFinite(n)) ? '–' : `${n}%`;
}

function renderFunnel(root, data) {
  root.innerHTML = '';

  if (data.unavailable === 'migration_pending') {
    const note = document.createElement('p');
    note.className = 'analytics-funnel-note';
    note.textContent = 'Waiting on migration 0098: no funnel events are being stored yet.';
    root.appendChild(note);
    return;
  }

  const total = data.steps[0]?.count || 0;

  const table = document.createElement('table');
  table.className = 'analytics-funnel-table';

  const caption = document.createElement('p');
  caption.className = 'analytics-funnel-caption';
  // The description moved out of the <table> as a sibling <p> so it can wrap
  // (a <caption> follows the wider-than-viewport table width and clips). Keep
  // its programmatic tie to the table via aria-labelledby so screen-reader
  // users still hear the funnel label, window, and counting unit on entry.
  // root ids are unique per funnel (an-funnel-activation / an-funnel-multiplayer).
  caption.id = `${root.id || 'an-funnel'}-desc`;
  table.setAttribute('aria-labelledby', caption.id);
  // Naming the unit matters most for the multiplayer funnel: one room can carry
  // several invitations, so a per-room count is NOT a per-invite count and must
  // never be read as one.
  const unitPlural = data.unit === 'room' ? 'room attempts' : 'page-lifetime attempts';
  // The caption is prose and must wrap to the visible width; the TABLE is what
  // scrolls horizontally on a phone. Keeping it as a <caption> inside the table
  // tied it to the (wider-than-viewport) table width and clipped it, so it's a
  // sibling <p> above a dedicated scroll wrapper instead.
  caption.textContent = `${data.label}: last ${data.days} UTC calendar days. `
    + `Counts are distinct ${unitPlural}, correlated by `
    + `${data.correlate_on === 'room_code' ? 'room code' : 'page-lifetime id'}; `
    + `"Events" is raw occurrences.`;

  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const label of ['Step', data.unit === 'room' ? 'Rooms' : 'Attempts', 'Events', 'Of first', 'Drop']) {
    const th = document.createElement('th');
    th.scope = 'col';
    th.textContent = label;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const step of data.steps) {
    const tr = document.createElement('tr');
    // An optional step is a branch off the chain, not a link in it — styled and
    // labelled apart so its absence is never read as a drop-off.
    if (step.optional) tr.className = 'analytics-funnel-branch';

    const th = document.createElement('th');
    th.scope = 'row';
    th.className = 'analytics-funnel-step';
    // Proportional bar under the label, drawn as a fill inside a visible track
    // so a small value reads as a small share of the whole rather than a stray
    // dash. Decorative only — the width duplicates the "Of first" column — so
    // the whole track is aria-hidden.
    const track = document.createElement('span');
    track.className = 'analytics-funnel-track';
    track.setAttribute('aria-hidden', 'true');
    const bar = document.createElement('span');
    bar.className = 'analytics-funnel-bar';
    bar.style.width = total > 0 ? `${(step.count / total) * 100}%` : '0';
    track.appendChild(bar);
    th.appendChild(track);
    const name = document.createElement('span');
    name.className = 'analytics-funnel-label';
    name.textContent = step.optional ? `↳ ${step.label}` : step.label;
    // The description explains what the step actually measures; the source tag
    // explains how much to trust the number.
    name.title = `${step.description} (recorded ${step.emitted_by}-side)`;
    th.appendChild(name);
    const src = document.createElement('span');
    src.className = 'analytics-funnel-source';
    src.textContent = step.emitted_by === 'server' ? 'server' : 'client';
    th.appendChild(src);
    if (step.optional) {
      const tag = document.createElement('span');
      tag.className = 'analytics-funnel-source';
      tag.textContent = 'optional';
      th.appendChild(tag);
    }
    tr.appendChild(th);

    // A branch's share is against the step it hangs off, not the funnel's first
    // step, and it has no drop of its own.
    const share = step.optional ? step.pct_of_branch_base : step.pct_of_first;
    const cells = [
      fmtInt(step.count),
      fmtInt(step.events),
      fmtPct(share),
      step.optional ? '–' : fmtPct(step.drop_pct_from_prev),
    ];
    for (const text of cells) {
      const td = document.createElement('td');
      td.textContent = text;
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  root.appendChild(caption);
  const scroll = document.createElement('div');
  scroll.className = 'analytics-funnel-scroll';
  scroll.appendChild(table);
  root.appendChild(scroll);

  const notes = [];
  // The per-occurrence transition is the headline number the distinct-unit chain
  // cannot express: five invites to one room are one room but five invites, so
  // the chain would read a single acceptance as 100%.
  const t = data.first_transition_by_event;
  if (t && t.from_events > 0 && t.from_events !== t.to_events) {
    const stepLabel = (n) => data.steps.find((s) => s.step === n)?.label || n;
    notes.push(
      `Per occurrence: ${fmtInt(t.to_events)} of ${fmtInt(t.from_events)} `
      + `${stepLabel(t.from).toLowerCase()} events reached ${stepLabel(t.to).toLowerCase()} `
      + `(${fmtPct(t.pct)}). One ${data.unit} can carry several, so this is the `
      + `per-event rate and the chain above is the per-${data.unit} rate.`
    );
  }
  const branches = data.steps.filter((s) => s.optional);
  if (branches.length) {
    notes.push(
      `${branches.map((s) => s.label).join(', ')} ${branches.length === 1 ? 'is' : 'are'} `
      + `optional (launches from the home page, the queue, or a deep link skip `
      + `${branches.length === 1 ? 'it' : 'them'}), so ${branches.length === 1 ? 'it is' : 'they are'} `
      + `shown as a branch and excluded from the drop-off chain.`
    );
  }
  if (data.returning) {
    notes.push(
      `Return rate: ${fmtInt(data.returning.returned)} of ${fmtInt(data.returning.players)} `
      + `players (${fmtPct(data.returning.pct)}) played on more than one day. `
      + 'Signed-in players only: the session correlation id is deliberately not durable.'
    );
  }
  if (data.uncorrelated > 0) {
    const firstRequired = data.steps.find((s) => !s.optional);
    notes.push(
      `${fmtInt(data.uncorrelated)} ${data.unit === 'room' ? 'room attempts' : 'page-lifetime attempts'} `
      + `recorded a later step but never "${firstRequired?.label}", so they are excluded above.`
    );
  }
  for (const text of notes) {
    const p = document.createElement('p');
    p.className = 'analytics-funnel-note';
    p.textContent = text;
    root.appendChild(p);
  }
}

async function loadFunnel(name, days) {
  const root = document.getElementById(`an-funnel-${name}`);
  if (!root) return;
  setPanelState(root, `Loading ${name} funnel…`);
  try {
    renderFunnel(root, await fetchJson(`/api/analytics/funnels/${name}?days=${days}`));
  } catch (err) {
    console.warn(`[analytics] funnel ${name} load failed:`, err);
    setPanelState(root, `Could not load the ${name} funnel.`, () => loadFunnel(name, days));
  }
}

let _worldTopo = null;
let _usTopo = null;

export function renderGeographyTable(root, data, labelForRow) {
  if (!root) return;
  root.replaceChildren();
  const scroll = document.createElement('div');
  scroll.className = 'analytics-geo-table-scroll';
  const table = document.createElement('table');
  table.className = 'analytics-geo-table';
  const caption = document.createElement('caption');
  caption.textContent = 'Source values for the map. Select a column heading to sort.';
  table.appendChild(caption);
  const thead = document.createElement('thead');
  const head = document.createElement('tr');
  const columns = [
    { label: 'Region', key: 'region' },
    { label: 'Player sessions', key: 'sessions' },
    { label: 'Signed-in players', key: 'players' },
    { label: 'Play time', key: 'total_seconds' },
  ];
  let sortKey = 'sessions';
  let sortDirection = 'desc';
  const buttons = new Map();
  for (const column of columns) {
    const th = document.createElement('th');
    th.scope = 'col';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'analytics-geo-sort';
    button.textContent = column.label;
    button.dataset.sortKey = column.key;
    buttons.set(column.key, { button, th });
    th.appendChild(button);
    head.appendChild(th);
  }
  thead.appendChild(head);
  table.appendChild(thead);
  const tbody = document.createElement('tbody');
  const renderRows = () => {
    tbody.replaceChildren();
    const enriched = data.map(row => ({ ...row, region: labelForRow(row) }));
    enriched.sort((a, b) => {
      const left = a[sortKey] || 0;
      const right = b[sortKey] || 0;
      const compared = sortKey === 'region'
        ? String(left).localeCompare(String(right))
        : Number(left) - Number(right);
      return sortDirection === 'asc' ? compared : -compared;
    });
    for (const row of enriched) {
      const tr = document.createElement('tr');
      const name = document.createElement('th');
      name.scope = 'row';
      name.textContent = row.region;
      tr.appendChild(name);
      for (const text of [fmtInt(row.sessions), fmtInt(row.players), fmtDuration(row.total_seconds)]) {
        const td = document.createElement('td');
        td.textContent = text;
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    for (const [key, entry] of buttons) {
      const { button, th } = entry;
      const active = key === sortKey;
      th.setAttribute('aria-sort', active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none');
      button.textContent = columns.find(column => column.key === key).label + (active ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');
    }
  };
  for (const { button } of buttons.values()) {
    button.addEventListener('click', () => {
      const key = button.dataset.sortKey;
      if (key === sortKey) sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDirection = key === 'region' ? 'asc' : 'desc';
      }
      renderRows();
    });
  }
  renderRows();
  table.appendChild(tbody);
  scroll.appendChild(table);
  root.appendChild(scroll);
}

async function loadGeography(request = currentWindowRequest()) {
  const { days, signal } = request;
  const mapRoot = document.getElementById('an-geo-map');
  const legend = document.getElementById('an-geo-legend');
  const tooltip = document.getElementById('an-geo-tooltip');
  const tableRoot = document.getElementById('an-geo-table');
  if (!mapRoot || staleWindowRequest(request)) return;
  setPanelState(mapRoot, 'Loading signed-in player geography…');
  if (legend) legend.replaceChildren();
  if (tableRoot) setPanelState(tableRoot, 'Loading geography source values…');
  let dataLoaded = false;
  try {
    const data = await fetchJson(`/api/analytics/geography?days=${days}`, { signal });
    if (staleWindowRequest(request)) return;
    if (!data.length) {
      setPanelState(mapRoot, `No signed-in player geography in the last ${days} days.`);
      if (tableRoot) setPanelState(tableRoot, `No geography source values in the last ${days} days.`);
      return;
    }
    dataLoaded = true;
    const countryName = (() => {
      try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; }
    })();
    const countryLabel = (code, fallback = 'Unknown') => {
      try { return countryName?.of(code) || fallback || code; } catch { return fallback || code; }
    };
    renderGeographyTable(tableRoot, data, (row) => row.location?.startsWith('US-')
      ? row.location
      : countryLabel(row.country || row.location, row.location));

    const { d3, topojson } = await loadMapLibs();
    if (staleWindowRequest(request)) return;
    const [worldTopo, usTopo] = await Promise.all([
      _worldTopo || loadWorldTopology(),
      _usTopo    || loadUsTopology(),
    ]);
    if (staleWindowRequest(request)) return;
    _worldTopo = worldTopo; _usTopo = usTopo;
    const byUsCode = new Map(); const byCountryCode = new Map(); let maxSessions = 0;
    for (const row of data) {
      if (row.sessions > maxSessions) maxSessions = row.sessions;
      if (row.country === 'US' && row.location.startsWith('US-')) byUsCode.set(row.location, row);
      else if (row.country) byCountryCode.set(row.country, row);
    }
    const NUMERIC_TO_ALPHA2 = buildNumericToAlpha2();
    const width = mapRoot.clientWidth || 800;
    const height = Math.round(width * 9 / 16);
    mapRoot.innerHTML = '';
    const svg = d3.select(mapRoot).append('svg').attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');
    const countries = topojson.feature(worldTopo, worldTopo.objects.countries);
    const projection = d3.geoNaturalEarth1().fitSize([width, height], countries);
    const path = d3.geoPath(projection);
    const colorScale = (sessions) => {
      if (!sessions || maxSessions === 0) return '#1a1a1f';
      const t = Math.sqrt(sessions / maxSessions);
      return `rgb(${Math.round(26+(0-26)*t)},${Math.round(26+(229-26)*t)},${Math.round(31+(160-31)*t)})`;
    };
    const showTip = (event, label, row) => {
      if (!tooltip) return;
      tooltip.replaceChildren();
      const name = document.createElement('div');
      name.className = 'analytics-geo-tooltip-name';
      name.textContent = label;
      const sessions = document.createElement('div');
      sessions.textContent = `${fmtInt(row?.sessions)} player session${row?.sessions === 1 ? '' : 's'}`;
      const players = document.createElement('div');
      players.textContent = `${fmtInt(row?.players)} signed-in player${row?.players === 1 ? '' : 's'}`;
      tooltip.append(name, sessions, players);
      tooltip.classList.remove('hidden');
      const rect = mapRoot.getBoundingClientRect();
      const targetRect = event.currentTarget?.getBoundingClientRect?.();
      const clientX = Number.isFinite(event.clientX) && event.clientX > 0
        ? event.clientX
        : (targetRect?.left || rect.left) + (targetRect?.width || 0) / 2;
      const clientY = Number.isFinite(event.clientY) && event.clientY > 0
        ? event.clientY
        : (targetRect?.top || rect.top) + (targetRect?.height || 0) / 2;
      tooltip.style.left = (clientX - rect.left) + 'px';
      tooltip.style.top  = (clientY - rect.top) + 'px';
    };
    const hideTip = () => tooltip?.classList.add('hidden');
    const countryRow = (d) => {
      if (String(d.id) === '840') return null;
      const code = NUMERIC_TO_ALPHA2[String(d.id)];
      return code ? byCountryCode.get(code) : null;
    };
    const countryPath = svg.append('g').selectAll('path').data(countries.features).join('path')
      .attr('class', 'analytics-geo-region').attr('d', path)
      .style('fill', d => colorScale(countryRow(d)?.sessions))
      .attr('tabindex', d => countryRow(d) ? 0 : null)
      .attr('role', d => countryRow(d) ? 'img' : null)
      .attr('aria-label', d => {
        const row = countryRow(d);
        if (!row) return null;
        const code = NUMERIC_TO_ALPHA2[String(d.id)];
        return `${countryLabel(code, d.properties?.name)}: ${fmtInt(row.sessions)} player sessions, ${fmtInt(row.players)} signed-in players`;
      });
    const showCountry = (event, d) => {
      const row = countryRow(d);
      if (!row) return;
      const code = NUMERIC_TO_ALPHA2[String(d.id)];
      showTip(event, countryLabel(code, d.properties?.name), row);
    };
    countryPath.on('mousemove', showCountry).on('focus', showCountry).on('click', showCountry)
      .on('mouseleave', hideTip).on('blur', hideTip)
      .on('keydown', (event) => { if (event.key === 'Escape') hideTip(); });
    const states = topojson.feature(usTopo, usTopo.objects.states);
    const usInsetWidth = width * 0.32; const usInsetHeight = usInsetWidth * 0.6;
    const usX = 8; const usY = height - usInsetHeight - 8;
    const usProjection = d3.geoAlbersUsa().fitSize([usInsetWidth, usInsetHeight], states);
    const usPath = d3.geoPath(usProjection);
    const usG = svg.append('g').attr('transform', `translate(${usX}, ${usY})`);
    usG.append('rect').attr('x',-4).attr('y',-4).attr('width',usInsetWidth+8).attr('height',usInsetHeight+8).attr('fill','#0e0e12').attr('stroke','#2a2a2a').attr('rx',4);
    usG.append('text').attr('x',0).attr('y',-6).attr('fill','#888').attr('font-family','JetBrains Mono, monospace').attr('font-size',9).text('United States');
    const FIPS_TO_USPS = buildFipsToUsps();
    const stateRow = (d) => {
      const code = FIPS_TO_USPS[String(d.id).padStart(2, '0')];
      return code ? byUsCode.get(`US-${code}`) : null;
    };
    const statePath = usG.selectAll('path').data(states.features).join('path')
      .attr('class', 'analytics-geo-region').attr('d', usPath)
      .style('fill', d => colorScale(stateRow(d)?.sessions))
      .attr('tabindex', d => stateRow(d) ? 0 : null)
      .attr('role', d => stateRow(d) ? 'img' : null)
      .attr('aria-label', d => {
        const row = stateRow(d);
        if (!row) return null;
        return `US · ${d.properties?.name || 'Unknown'}: ${fmtInt(row.sessions)} player sessions, ${fmtInt(row.players)} signed-in players`;
      });
    const showState = (event, d) => {
      const row = stateRow(d);
      if (row) showTip(event, `US · ${d.properties?.name || 'Unknown'}`, row);
    };
    statePath.on('mousemove', showState).on('focus', showState).on('click', showState)
      .on('mouseleave', hideTip).on('blur', hideTip)
      .on('keydown', (event) => { if (event.key === 'Escape') hideTip(); });
    svg.attr('role', 'group')
      .attr('aria-label', `Player sessions by region for the last ${days} days. Focus a populated region for details.`)
      .attr('aria-describedby', 'an-geo-legend');
    if (legend) {
      legend.replaceChildren();
      const zero = document.createElement('span');
      zero.textContent = '0';
      const bar = document.createElement('div');
      bar.className = 'analytics-geo-legend-bar';
      bar.setAttribute('aria-hidden', 'true');
      const midpoint = document.createElement('span');
      midpoint.textContent = `${fmt1(maxSessions * 0.25)} sessions at midpoint (25% of max)`;
      const maximum = document.createElement('span');
      maximum.textContent = `${fmtInt(maxSessions)} player sessions`;
      legend.append(zero, bar, midpoint, maximum);
    }
    const stateNames = new Map(states.features.map((feature) => [
      FIPS_TO_USPS[String(feature.id).padStart(2, '0')],
      feature.properties?.name,
    ]));
    renderGeographyTable(tableRoot, data, (row) => {
      if (row.location?.startsWith('US-')) {
        const code = row.location.slice(3);
        return `US · ${stateNames.get(code) || code}`;
      }
      return countryLabel(row.country || row.location, row.location);
    });
  } catch (err) {
    if (isAborted(err, request)) return;
    console.warn('[analytics] geography load failed:', err);
    setPanelState(mapRoot, 'Could not load geography or its map assets.', () => loadGeography(request));
    if (!dataLoaded && tableRoot) setPanelState(tableRoot, 'Could not load geography source values.', () => loadGeography(request));
  }
}

function buildNumericToAlpha2() {
  return {'004':'AF','008':'AL','012':'DZ','020':'AD','024':'AO','028':'AG','031':'AZ','032':'AR','036':'AU','040':'AT','044':'BS','048':'BH','050':'BD','051':'AM','052':'BB','056':'BE','060':'BM','064':'BT','068':'BO','070':'BA','072':'BW','076':'BR','084':'BZ','090':'SB','096':'BN','100':'BG','104':'MM','108':'BI','112':'BY','116':'KH','120':'CM','124':'CA','132':'CV','140':'CF','144':'LK','148':'TD','152':'CL','156':'CN','158':'TW','170':'CO','174':'KM','178':'CG','180':'CD','188':'CR','191':'HR','192':'CU','196':'CY','203':'CZ','204':'BJ','208':'DK','212':'DM','214':'DO','218':'EC','222':'SV','226':'GQ','231':'ET','232':'ER','233':'EE','242':'FJ','246':'FI','250':'FR','258':'PF','262':'DJ','266':'GA','268':'GE','270':'GM','275':'PS','276':'DE','288':'GH','292':'GI','300':'GR','308':'GD','320':'GT','324':'GN','328':'GY','332':'HT','336':'VA','340':'HN','348':'HU','352':'IS','356':'IN','360':'ID','364':'IR','368':'IQ','372':'IE','376':'IL','380':'IT','384':'CI','388':'JM','392':'JP','398':'KZ','400':'JO','404':'KE','408':'KP','410':'KR','414':'KW','417':'KG','418':'LA','422':'LB','426':'LS','428':'LV','430':'LR','434':'LY','438':'LI','440':'LT','442':'LU','450':'MG','454':'MW','458':'MY','462':'MV','466':'ML','470':'MT','478':'MR','480':'MU','484':'MX','492':'MC','496':'MN','498':'MD','499':'ME','504':'MA','508':'MZ','512':'OM','516':'NA','520':'NR','524':'NP','528':'NL','540':'NC','548':'VU','554':'NZ','558':'NI','562':'NE','566':'NG','578':'NO','586':'PK','591':'PA','598':'PG','600':'PY','604':'PE','608':'PH','616':'PL','620':'PT','624':'GW','626':'TL','630':'PR','634':'QA','642':'RO','643':'RU','646':'RW','682':'SA','686':'SN','688':'RS','690':'SC','694':'SL','702':'SG','703':'SK','704':'VN','705':'SI','706':'SO','710':'ZA','716':'ZW','724':'ES','728':'SS','729':'SD','740':'SR','748':'SZ','752':'SE','756':'CH','760':'SY','762':'TJ','764':'TH','768':'TG','776':'TO','780':'TT','784':'AE','788':'TN','792':'TR','795':'TM','798':'TV','800':'UG','804':'UA','807':'MK','818':'EG','826':'GB','834':'TZ','840':'US','854':'BF','858':'UY','860':'UZ','862':'VE','882':'WS','887':'YE','894':'ZM','535':'BQ','999':'XX'};
}

function buildFipsToUsps() {
  return {'01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE','11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA','20':'KS','21':'KY','22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN','28':'MS','29':'MO','30':'MT','31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM','36':'NY','37':'NC','38':'ND','39':'OH','40':'OK','41':'OR','42':'PA','44':'RI','45':'SC','46':'SD','47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA','54':'WV','55':'WI','56':'WY','60':'AS','66':'GU','69':'MP','72':'PR','78':'VI'};
}

// ── Entry point ──────────────────────────────────────────

let _bound = false;

function activeFunnelDays() {
  return document.querySelector('.analytics-tab[data-funnel-days].active')?.dataset.funnelDays || '30';
}

async function loadWindowedAnalyticsData() {
  const days = activeAnalyticsDays();
  const request = windowLoads.begin(days);
  updateWindowLabels();
  return settleAnalyticsRequests([
    () => loadDau(request),
    () => loadHeatmap(request),
    () => loadTopGames(
      document.querySelector('.analytics-tab[data-topgames-by].active')?.dataset.topgamesBy || 'players',
      request,
    ),
    () => loadSystems(request),
    () => loadNetplay(request),
    () => loadNetplayRtt(request),
    () => loadAchievements(request),
    () => loadSignups(request),
    () => loadGeography(request),
    () => loadPageViews(request),
  ]);
}

async function loadAnalyticsData() {
  const funnelDays = activeFunnelDays();
  const days = activeAnalyticsDays();
  const request = windowLoads.begin(days);
  updateWindowLabels();
  return settleAnalyticsRequests([
    () => loadRefreshStatus(),
    () => loadSummary(),
    () => loadFunnel('activation', funnelDays),
    () => loadFunnel('multiplayer', funnelDays),
    () => loadDau(request),
    () => loadHeatmap(request),
    () => loadTopGames(
      document.querySelector('.analytics-tab[data-topgames-by].active')?.dataset.topgamesBy || 'players',
      request,
    ),
    () => loadSystems(request),
    () => loadNetplay(request),
    () => loadNetplayRtt(request),
    () => loadAchievements(request),
    () => loadSignups(request),
    () => loadGeography(request),
    () => loadPageViews(request),
  ]);
}

export function render() {
  if (!_bound) {
    _bound = true;
    document.querySelectorAll('.analytics-tab[data-topgames-by]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.analytics-tab[data-topgames-by]').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        settleAnalyticsRequests([
          () => loadTopGames(btn.dataset.topgamesBy, currentWindowRequest()),
        ]);
      });
    });
    document.getElementById('an-refresh-btn')?.addEventListener('click', triggerRefresh);
    document.querySelectorAll('[data-analytics-days]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('[data-analytics-days]').forEach(other => {
          other.classList.remove('active');
          other.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        analyticsDays = Number(btn.dataset.analyticsDays);
        loadWindowedAnalyticsData();
      });
    });
    // One window control drives both funnels — comparing activation against
    // invites over different periods would be misleading.
    document.querySelectorAll('.analytics-tab[data-funnel-days]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.analytics-tab[data-funnel-days]').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        const days = btn.dataset.funnelDays;
        settleAnalyticsRequests([
          () => loadFunnel('activation', days),
          () => loadFunnel('multiplayer', days),
        ]);
      });
    });
  }

  activeAnalyticsDays();
  updateWindowLabels();
  loadAnalyticsData();
}
